import { useState } from "react";
import { X, Flag } from "lucide-react";
import toast from "react-hot-toast";
import { useLang } from "../context/LangContext";
import { questionReportsAPI } from "../api";

type ReportType = "wrong_answer" | "typo" | "comment";

interface ReportQuestionModalProps {
  questionId: number;
  onClose: () => void;
}

export default function ReportQuestionModal({
  questionId,
  onClose,
}: ReportQuestionModalProps) {
  const { t } = useLang();
  const [types, setTypes] = useState<ReportType[]>([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleType = (type: ReportType) => {
    setTypes((prev) =>
      prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type],
    );
  };

  const handleSubmit = async () => {
    if (types.length === 0) {
      toast.error(t("reportSelectType"));
      return;
    }
    setSubmitting(true);
    try {
      await questionReportsAPI.create({ questionId, types, comment });
      toast.success(t("reportSuccess"));
      onClose();
    } catch {
      toast.error(t("reportError"));
    } finally {
      setSubmitting(false);
    }
  };

  const options: { key: ReportType; label: string }[] = [
    { key: "wrong_answer", label: t("reportWrongAnswer") },
    { key: "typo", label: t("reportTypo") },
    { key: "comment", label: t("reportComment") },
  ];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,.7)" }}
      onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-2xl p-5"
        style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-white font-bold text-base">
            <Flag size={16} />
            {t("reportQuestion")}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col gap-2 mb-3">
          {options.map((opt) => (
            <label
              key={opt.key}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer select-none"
              style={{
                background:
                  types.includes(opt.key) ?
                    "rgba(59,130,246,.1)"
                  : "var(--card2)",
                border: `1px solid ${types.includes(opt.key) ? "rgba(59,130,246,.4)" : "var(--border)"}`,
              }}>
              <input
                type="checkbox"
                checked={types.includes(opt.key)}
                onChange={() => toggleType(opt.key)}
                className="w-4 h-4 accent-blue-500"
              />
              <span className="text-sm text-white/90">{opt.label}</span>
            </label>
          ))}
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("reportCommentPlaceholder")}
          rows={3}
          className="w-full rounded-xl px-3 py-2.5 text-sm text-white/90 outline-none resize-none mb-4"
          style={{ background: "var(--card2)", border: "1px solid var(--border)" }}
        />

        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-2.5 rounded-xl font-bold text-sm text-white transition-opacity disabled:opacity-50"
          style={{ background: "#3B82F6" }}>
          {t("reportSubmit")}
        </button>
      </div>
    </div>
  );
}
